'use client';

import clsx from 'clsx';
import {
	BookOpen,
	ChevronDown,
	LogOut,
	Pin,
	RotateCcw,
	SkipForward,
} from 'lucide-react';
import {
	type ForwardedRef,
	forwardRef,
	type ReactNode,
	useCallback,
	useEffect,
	useImperativeHandle,
	useRef,
	useState,
} from 'react';

import type { ChatMessage, TaskFile } from '@/entities/interview-task';
import { isSkipCommand } from '@/features/candidate-interview';
import type { AIVisibleMessageV1 } from '@/shared/api';
import { chatStyles } from '@/shared/ui/chat/styles';

import type { DialogCommand } from '../lib/commands';
import { isExitCommand } from '../lib/commands';
import type { ControlsTrack } from '../lib/controls-analytics';
import type { AttachStatus } from '../lib/status-palette';
import type { BreadcrumbItem } from './breadcrumbs';
import { ChatFooter, ChatScrollArea, ChatTranscript } from './chat-layout';
import { CompletedBanner } from './completed-banner';
import { Composer } from './composer';
import { DialogCompletedActions } from './dialog-completed-actions';
import { type DialogNavigation, DialogTopbar } from './dialog-topbar';
import { Message } from './message';
import { PreStartGate } from './pre-start-gate';
import { ReportPendingBanner } from './report-pending-banner';
import { ThinkingRow } from './thinking-row';

const noopTrack: ControlsTrack = () => {};
// Match the composer: 12 px gutters on mobile, 24 px on desktop, 720 px content.
const messageWidth = chatStyles.messageWidth;

type ChatPaneProps = {
	navigation?: DialogNavigation;
	title?: string;
	track?: ControlsTrack;
	solutionControl?: { ready: boolean; error: boolean; onOpen: () => void };
	/** Historical messages use the same chat UI without live controls. */
	readOnly?: boolean;
	readOnlyFooter?: ReactNode;
	isInterview: boolean;
	messages: ChatMessage[];
	/** SSR-список сообщений: то, что было на момент загрузки страницы.
	 *  Они НЕ анимируются — typewriter работает только для приходящих позже. */
	initialMessages: AIVisibleMessageV1[];
	isThinking: boolean;
	latestFileMsgIdx: number;
	pinnedTitle: string | null;
	pinnedBarShown: boolean;
	breadcrumbs?: BreadcrumbItem[];
	hasCompletedBanner: boolean;
	/** Диалог остановлен вручную — показываем баннер остановки вместо ввода. */
	hasStoppedBanner: boolean;
	candidateMode?: boolean;
	candidateControls?: {
		progress: { current: number; total: number };
		onSkip: () => Promise<void | boolean>;
		onFinish: () => Promise<void | boolean>;
		autoNextSeconds?: number;
		totalTime: string;
		closed: boolean;
		timedOut: boolean;
		busy: boolean;
	};
	canSendMessage: boolean;
	/** Диалог в статусе компакции — composer не показывает «AI печатает». */
	isCompaction: boolean;
	/** Диалог в статусе формирования отчёта — вместо ввода показываем баннер ожидания. */
	isReportPending: boolean;
	isInterviewStarted: boolean;
	timerMm: string;
	timerSs: string;
	timerLow: boolean;
	onOpenFile: (file: TaskFile) => void;
	onOpenQuiz: (msg: ChatMessage) => void;
	getAttachStatus: (
		m: ChatMessage,
		idx: number,
		file: TaskFile,
	) => AttachStatus | null;
	getQuizStatus: (msg: ChatMessage) => 'pending' | 'accepted' | null;
	onRate: (messageId: number, sentiment: 'up' | 'down') => void;
	draft: string;
	onDraftChange: (v: string) => void;
	onSend: () => void;
	onResetDialog: DialogCommand['run'];
	onNext: () => void;
	nextLabel: string;
	/** Ссылки на соседние задачи курса для стрелок навигации (undefined → стрелка неактивна). */
	coursePrevHref?: string;
	courseNextHref?: string;
	onCoursePrev: () => void;
	onCourseNext: () => void;
	onLogout: DialogCommand['run'];
	/** Завершить интервью можно только из idle — иначе кнопка неактивна. */
	logoutDisabled: boolean;
	onStartInterview: () => void;
};

export type ChatPaneHandle = {
	/** Прокрутить чат к сообщению и подсветить его файлы (как scrollToPinned). */
	scrollToMessage: (msgIdx: number) => void;
};

function ChatPaneInner(
	{
		readOnly = false,
		navigation,
		title,
		track = noopTrack,
		solutionControl,
		readOnlyFooter,
		isInterview,
		messages,
		initialMessages,
		isThinking,
		latestFileMsgIdx,
		pinnedTitle,
		breadcrumbs,
		hasCompletedBanner,
		hasStoppedBanner,
		candidateMode,
		candidateControls,
		canSendMessage,
		isCompaction,
		isReportPending,
		isInterviewStarted,
		timerMm,
		timerSs,
		timerLow,
		onOpenFile,
		onOpenQuiz,
		getAttachStatus,
		getQuizStatus,
		onRate,
		draft,
		onDraftChange,
		onSend,
		onResetDialog,
		onNext,
		nextLabel,
		coursePrevHref,
		courseNextHref,
		onCoursePrev,
		onCourseNext,
		onLogout,
		logoutDisabled,
		onStartInterview,
	}: ChatPaneProps,
	ref: ForwardedRef<ChatPaneHandle>,
) {
	const scrollRef = useRef<HTMLDivElement>(null);
	const messageRefs = useRef<Record<number, HTMLDivElement | null>>({});
	const [highlightedMsgIdx, setHighlightedMsgIdx] = useState<number | null>(
		null,
	);
	const highlightTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);

	// Какие сообщения уже «видели» — для них typewriter-анимации не делаем.
	// Сидим из SSR-списка `initialMessages` (всё, что пришло с сервера на
	// момент загрузки страницы) — тогда перезагрузка показывает диалог сразу.
	// Store наполняется через useEffect позже, поэтому из `messages` сидить
	// нельзя: первая отрисовка получает пустой массив.
	// clientId важнее messageId: оптимистичные сообщения переходят
	// clientId → (clientId + messageId), и ключ должен оставаться тем же,
	// иначе React пересоздаёт обёртку и `.ai-msg-enter` срабатывает второй раз.
	const getMessageKey = (m: ChatMessage, i: number): string => {
		if (m.clientId) return `c:${m.clientId}`;
		if (m.messageId !== undefined) return `s:${m.messageId}`;
		return `i:${i}`;
	};
	const seenKeysRef = useRef<Set<string>>(new Set());
	const lastSeedRef = useRef<AIVisibleMessageV1[] | null>(null);
	// Реф-сравнение: при смене диалога (Пройти заново) приходит новый
	// initialMessages — пересеиваем сет.
	if (lastSeedRef.current !== initialMessages) {
		seenKeysRef.current = new Set(initialMessages.map((m) => `s:${m.id}`));
		lastSeedRef.current = initialMessages;
	}

	useEffect(() => {
		const seen = seenKeysRef.current;
		messages.forEach((m, i) => seen.add(getMessageKey(m, i)));
	}, [messages]);

	// Какие сообщения уже проигрывали анимацию входа (.ai-msg-enter). Нужно,
	// чтобы при откате оптимистичного сообщения (неудачная отправка) предыдущее
	// AI-сообщение, снова став последним, не анимировалось повторно.
	// НЕ сидируем из initialMessages: на первой загрузке последнее сообщение
	// должно появиться с анимацией, как и раньше.
	const enteredKeysRef = useRef<Set<string>>(new Set());
	useEffect(() => {
		if (messages.length === 0) return;
		const lastIdx = messages.length - 1;
		enteredKeysRef.current.add(getMessageKey(messages[lastIdx], lastIdx));
	}, [messages]);

	// «Прилипание» к низу: если пользователь сам отскроллил наверх — не дёргаем
	// его автоматическим скроллом. Порог ~80 px учитывает инерцию/округления.
	// Ref читают эффекты (синхронно), state управляет видимостью кнопки
	// «вниз», поэтому держим оба синхронно.
	const STICKY_THRESHOLD_PX = 80;
	const [isAtBottom, setIsAtBottom] = useState(true);
	const isStickyRef = useRef(true);
	isStickyRef.current = isAtBottom;

	useEffect(() => {
		const el = scrollRef.current;
		if (!el) return;
		const onScroll = () => {
			const atBottom =
				el.scrollHeight - el.scrollTop - el.clientHeight <= STICKY_THRESHOLD_PX;
			setIsAtBottom((prev) => (prev !== atBottom ? atBottom : prev));
		};
		el.addEventListener('scroll', onScroll, { passive: true });
		return () => el.removeEventListener('scroll', onScroll);
	}, []);

	const scrollToBottom = () => {
		const el = scrollRef.current;
		if (!el) return;
		el.scrollTo({ top: el.scrollHeight, behavior: 'smooth' });
	};

	// Логика автоскролла на новые сообщения:
	//   1) Я только что отправил сообщение (последнее — «you», ключ изменился)
	//      → всегда прыгаем к низу.
	//   2) Иначе скроллим, только если пользователь и так стоит у низа.
	const prevLastKeyRef = useRef<string | null>(null);
	useEffect(() => {
		const el = scrollRef.current;
		if (!el) return;
		const last = messages[messages.length - 1];
		const lastKey = last ? getMessageKey(last, messages.length - 1) : null;
		const userJustSent =
			!!last && last.role === 'you' && lastKey !== prevLastKeyRef.current;
		prevLastKeyRef.current = lastKey;
		if (userJustSent) {
			// Своё сообщение — пользователь ждёт мгновенной реакции, без задержек.
			el.scrollTop = el.scrollHeight;
			isStickyRef.current = true;
		} else if (isStickyRef.current) {
			// «AI печатает…» или новое сообщение от ИИ — мягко доезжаем до низа,
			// чтобы скролл не «прыгал» резко.
			el.scrollTo({ top: el.scrollHeight, behavior: 'smooth' });
		} else {
			// Контент сменился без автоскролла (например, «Пройти заново»
			// укорачивает диалог): scroll-событие не стреляет, поэтому пересчитываем
			// «у низа?» вручную — иначе кнопка «вниз» зависнет над коротким диалогом.
			const atBottom =
				el.scrollHeight - el.scrollTop - el.clientHeight <= STICKY_THRESHOLD_PX;
			setIsAtBottom((prev) => (prev !== atBottom ? atBottom : prev));
		}
	}, [messages, isThinking]);

	useEffect(
		() => () => {
			if (highlightTimerRef.current) clearTimeout(highlightTimerRef.current);
		},
		[],
	);

	// Прокрутка к сообщению + кратковременная подсветка его файлов.
	const scrollToMessage = useCallback((msgIdx: number) => {
		const el = msgIdx >= 0 ? messageRefs.current[msgIdx] : null;
		if (el) {
			el.scrollIntoView({ behavior: 'smooth', block: 'center' });
			if (highlightTimerRef.current) clearTimeout(highlightTimerRef.current);
			setHighlightedMsgIdx(msgIdx);
			highlightTimerRef.current = setTimeout(() => {
				setHighlightedMsgIdx(null);
				highlightTimerRef.current = null;
			}, 700);
		} else if (scrollRef.current) {
			scrollRef.current.scrollTo({
				top: scrollRef.current.scrollHeight,
				behavior: 'smooth',
			});
		}
	}, []);

	useImperativeHandle(ref, () => ({ scrollToMessage }), [scrollToMessage]);

	const scrollToPinned = () => scrollToMessage(latestFileMsgIdx);

	const composerWaitingForAi =
		!canSendMessage &&
		!hasCompletedBanner &&
		!hasStoppedBanner &&
		!isCompaction;
	const composerDisabled =
		(isInterview && !isInterviewStarted) || composerWaitingForAi;
	const composerPlaceholder =
		isInterview && !isInterviewStarted
			? 'Нажми «Начать», чтобы запустить интервью'
			: composerWaitingForAi
				? 'AI печатает ответ...'
				: undefined;

	const commands: DialogCommand[] = readOnly
		? []
		: [
				...(messages.length
					? [
							{
								id: 'task',
								label: 'К заданию',
								description: 'Перейти к текущему заданию',
								command: '/task',
								icon: Pin,
								run: scrollToPinned,
							},
						]
					: []),
				...(solutionControl
					? [
							{
								id: 'solution',
								label: 'Решение',
								description: solutionControl.error
									? 'Повторить загрузку решения'
									: 'Посмотреть идею и код решения',
								command: '/solution',
								icon: BookOpen,
								disabled: !solutionControl.ready && !solutionControl.error,
								run: solutionControl.onOpen,
							},
						]
					: []),
				...(!candidateMode
					? [
							{
								id: 'restart',
								label: 'Заново',
								description: 'Начать задачу заново',
								command: '/restart',
								icon: RotateCcw,
								disabled:
									isThinking ||
									isReportPending ||
									(!canSendMessage && !hasCompletedBanner && !hasStoppedBanner),
								run: onResetDialog,
							},
						]
					: []),
				...(candidateMode && candidateControls && !candidateControls.closed
					? [
							{
								id: 'next',
								label: 'Пропустить',
								description: 'Перейти к следующему заданию',
								command: '/next',
								aliases: ['/skip'],
								icon: SkipForward,
								disabled: candidateControls.busy,
								run: () => candidateControls.onSkip(),
							},
						]
					: []),
				...(isInterview &&
				!hasCompletedBanner &&
				!hasStoppedBanner &&
				!candidateControls?.closed
					? [
							{
								id: 'exit',
								label: 'Завершить интервью',
								command: '/exit',
								icon: LogOut,
								disabled: logoutDisabled || !!candidateControls?.busy,
								run: onLogout,
							},
						]
					: []),
			];
	// Temporarily hide task and solution actions in the input menu.
	const composerCommands = commands.filter(
		({ id }) => id !== 'task' && id !== 'solution',
	);
	const resolvedNavigation = navigation ?? {
		backHref:
			[...(breadcrumbs ?? [])].reverse().find((item) => item.href)?.href ??
			'/problems',
		backLabel: readOnly ? 'К результату' : 'К задачам',
	};
	return (
		<div className="flex h-full min-w-0 flex-col">
			<DialogTopbar
				navigation={{
					...resolvedNavigation,
					progress: candidateControls?.progress ?? resolvedNavigation.progress,
				}}
				title={
					title ?? breadcrumbs?.at(-1)?.label ?? pinnedTitle ?? 'ИИ-диалог'
				}
				previousHref={coursePrevHref}
				nextHref={courseNextHref}
				onPrevious={onCoursePrev}
				onNext={onCourseNext}
				onNavigate={(action) =>
					track('navigation', { action, stage: 'intent' })
				}
				candidate={candidateMode}
				readOnly={readOnly}
				timer={
					isInterview && !readOnly
						? {
								text: `${timerMm}:${timerSs}`,
								low: timerLow,
								totalTime: candidateControls?.totalTime,
							}
						: undefined
				}
			/>

			<div className="relative flex min-h-0 flex-1 flex-col">
				<ChatScrollArea scrollRef={scrollRef}>
					<ChatTranscript>
						{isInterview && !isInterviewStarted ? (
							<div className={messageWidth}>
								<PreStartGate onStart={onStartInterview} />
							</div>
						) : (
							messages.map((m, i) => {
								const key = getMessageKey(m, i);
								const shouldAnimate =
									m.role === 'interviewer' &&
									!!m.text &&
									!seenKeysRef.current.has(key);
								// .ai-msg-enter — один раз на сообщение: иначе при откате
								// оптимистичного сообщения предыдущее снова становится
								// последним и анимация входа запускается повторно.
								const animateEnter =
									i === messages.length - 1 && !enteredKeysRef.current.has(key);
								return (
									<div
										key={m.clientId ?? m.messageId ?? i}
										ref={(el) => {
											messageRefs.current[i] = el;
										}}
										className={clsx('w-full', animateEnter && 'ai-msg-enter')}
									>
										<Message
											contentClassName={messageWidth}
											m={m}
											onOpenFile={onOpenFile}
											onOpenQuiz={() => onOpenQuiz(m)}
											getAttachStatus={(file) => getAttachStatus(m, i, file)}
											quizStatus={getQuizStatus(m)}
											onRate={readOnly ? undefined : onRate}
											highlightFiles={highlightedMsgIdx === i}
											shouldAnimate={shouldAnimate}
											scrollContainerRef={scrollRef}
										/>
									</div>
								);
							})
						)}
						{isThinking && (
							<div className={messageWidth}>
								<ThinkingRow />
							</div>
						)}
					</ChatTranscript>
				</ChatScrollArea>
				{!isAtBottom && (
					<button
						type="button"
						onClick={scrollToBottom}
						aria-label="К новым сообщениям"
						title="К новым сообщениям"
						className="absolute bottom-3 left-1/2 flex size-9 -translate-x-1/2 cursor-pointer items-center justify-center rounded-full border border-gray-4 bg-white text-gray-3 shadow-md transition-colors hover:bg-gray-5 hover:text-black"
					>
						<ChevronDown size={18} strokeWidth={2} />
					</button>
				)}
			</div>

			<ChatFooter>
				{readOnly ? (
					readOnlyFooter
				) : candidateMode && candidateControls?.closed ? (
					<CompletedBanner
						onNext={onNext}
						nextLabel="Далее"
						nextDisabled={candidateControls.busy}
						message={
							candidateControls.timedOut
								? `Время на задачу вышло.${candidateControls.autoNextSeconds !== undefined ? ` Переход через ${candidateControls.autoNextSeconds} с.` : ''}`
								: 'Диалог завершён.'
						}
					/>
				) : candidateMode ? (
					<Composer
						commands={composerCommands}
						track={track}
						value={draft}
						onChange={onDraftChange}
						onSend={onSend}
						disabled={candidateControls?.busy}
						sendDisabled={
							!canSendMessage && !isSkipCommand(draft) && !isExitCommand(draft)
						}
					/>
				) : hasCompletedBanner ? (
					<CompletedBanner
						onReset={() => {
							track('command', {
								action: 'restart',
								source: 'button',
								stage: 'intent',
							});
							void onResetDialog();
						}}
						onNext={() => {
							track('navigation', { action: 'continue', stage: 'intent' });
							onNext();
						}}
						nextLabel={nextLabel}
					/>
				) : hasStoppedBanner ? (
					<DialogCompletedActions
						commands={commands}
						onNext={onNext}
						nextLabel={nextLabel}
						message="Интервью прервано."
						track={track}
					/>
				) : isReportPending ? (
					<ReportPendingBanner />
				) : (
					<Composer
						commands={composerCommands}
						track={track}
						value={draft}
						onChange={onDraftChange}
						onSend={onSend}
						disabled={composerDisabled}
						sendDisabled={!canSendMessage}
						disabledPlaceholder={composerPlaceholder}
					/>
				)}
				<div className="mt-2.5 text-center text-xs text-gray-3">
					Помни, что AI может совершать ошибки.
				</div>
			</ChatFooter>
		</div>
	);
}

export const ChatPane = forwardRef<ChatPaneHandle, ChatPaneProps>(
	ChatPaneInner,
);
ChatPane.displayName = 'ChatPane';
